import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { List, ChevronRight } from 'lucide-react';
import { TOCItem } from '../utils/tocGenerator';

interface TableOfContentsProps {
  items: TOCItem[];
  onItemClick?: (id: string) => void;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ items, onItemClick }) => {
  const [activeId, setActiveId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-80px 0px -80% 0px',
        threshold: 0
      }
    );

    const allIds: string[] = [];
    items.forEach((item) => {
      allIds.push(item.id);
      if (item.children) {
        item.children.forEach(child => allIds.push(child.id));
      }
    });

    allIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [items]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setIsOpen(false);
      onItemClick?.(id);
    }
  };

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  if (items.length === 0) return null;

  const renderItem = (item: TOCItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);

    return (
      <li key={item.id}>
        <div className="flex items-center">
          {hasChildren && (
            <button
              onClick={(e) => toggleExpand(item.id, e)}
              className="mr-0.5 p-0.5 hover:bg-gray-100 rounded transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <ChevronRight
                size={14}
                className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              />
            </button>
          )}
          <button
            onClick={() => handleClick(item.id)}
            className={`block text-sm leading-snug transition-colors text-left flex-1 py-1 px-0 ${
              !hasChildren ? 'ml-[18px]' : ''
            } ${
              activeId === item.id
                ? 'text-ctc-blue font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {item.text}
          </button>
        </div>

        {hasChildren && isExpanded && (
          <AnimatePresence>
            <motion.ul
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="ml-[18px] mt-1 space-y-1 border-l-2 border-gray-100 pl-2"
            >
              {item.children!.map((child) => (
                <li key={child.id}>
                  <button
                    onClick={() => handleClick(child.id)}
                    className={`block text-sm leading-snug transition-colors text-left w-full py-1 px-0 ${
                      activeId === child.id
                        ? 'text-ctc-blue font-medium'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {child.text}
                  </button>
                </li>
              ))}
            </motion.ul>
          </AnimatePresence>
        )}
      </li>
    );
  };

  return (
    <>
      <div className="hidden lg:block sticky top-24 h-fit">
        <div className="pr-0">
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-2">
            Contents
          </h3>
          <nav>
            <ul className="space-y-1">
              {items.map((item) => renderItem(item))}
            </ul>
          </nav>
        </div>
      </div>

      <div className="lg:hidden mb-6">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors w-full"
        >
          <List size={18} />
          <span className="font-medium text-gray-900">Contents</span>
        </button>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 border border-gray-200 rounded-lg bg-white overflow-hidden"
          >
            <nav className="p-2">
              <ul className="space-y-1">
                {items.map((item) => renderItem(item))}
              </ul>
            </nav>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default TableOfContents;
